/**
 * Engagement funnel — true funnel shape (trapezoidal segments stacked
 * vertically, narrowing from top to bottom).
 *
 * Stages: sends → delivered → unique reach → replies → projected
 * enrollments. Each segment shows count + conversion vs the prior stage.
 * The bottom (projection) segment also renders a ±15% confidence band
 * shading around its midline. Pure-SVG, no chart library.
 */

import React from 'react';

interface FederationRow {
  sends?: number | null;
  delivered?: number | null;
  replies?: number | null;
  unique_members_engaged?: number | null;
}

interface Props {
  row: FederationRow | null | undefined;
  projectedEnrollments?: number | null;
  enrollmentRatio?: number | null;
  enrollmentLabel?: string | null;
}

const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v: number): string => v.toLocaleString();

const pctOf = (n: number, d: number): string =>
  d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '—';

export const EngagementFunnelChart: React.FC<Props> = ({
  row,
  projectedEnrollments,
  enrollmentRatio,
  enrollmentLabel,
}) => {
  if (!row) return null;

  const sends = num(row.sends);
  const delivered = num(row.delivered);
  const reach = num(row.unique_members_engaged);
  const replies = num(row.replies);
  const enrollments = num(projectedEnrollments);

  if (sends <= 0) {
    return (
      <div className="text-xs text-gray-500">
        No engagement data for this federation.
      </div>
    );
  }

  const stages = [
    { label: 'Sends', value: sends, color: '#6366f1', prior: null as number | null }, // indigo-500
    { label: 'Delivered', value: delivered, color: '#818cf8', prior: sends }, // indigo-400
    { label: 'Unique reach', value: reach, color: '#a855f7', prior: delivered }, // purple-500
    { label: 'Replies', value: replies, color: '#10b981', prior: reach }, // emerald-500
    {
      label: enrollmentLabel?.replace(/_/g, ' ') ?? 'Projected enrollments',
      value: enrollments,
      color: '#059669', // emerald-600
      prior: replies,
      isProjection: true,
    },
  ];

  // Funnel widths scale log so the tail is visible. Min ~6% width to keep
  // bottom segments readable even when conversion is very low.
  const maxV = Math.max(...stages.map((s) => s.value));
  const widthFrac = (v: number): number => {
    if (maxV <= 0 || v <= 0) return 0;
    // Log-scale, then clamp to [0.06, 1.0]
    const f = Math.log(v + 1) / Math.log(maxV + 1);
    return Math.max(0.06, Math.min(1, f));
  };

  // Layout: SVG split into two columns. Left column = funnel shape.
  // Right column = stage label + value + conversion text aligned to the
  // vertical center of each segment.
  const segH = 56;
  const segGap = 4;
  const funnelW = 280;
  const textW = 200;
  const totalW = funnelW + 16 + textW;
  const totalH = stages.length * (segH + segGap) - segGap + 12;
  const funnelCx = funnelW / 2;

  // Compute top/bottom half-widths for each trapezoid. Each segment's TOP
  // half-width = funnelW/2 × widthFrac(value); BOTTOM half-width =
  // funnelW/2 × widthFrac(next.value). This produces continuous edges.
  type TrapGeom = { topHW: number; botHW: number; y0: number; y1: number };
  const geoms: TrapGeom[] = stages.map((s, i) => {
    const topHW = (funnelW / 2) * widthFrac(s.value);
    const nextV = stages[i + 1]?.value ?? s.value * 0.6;
    const botHW = (funnelW / 2) * widthFrac(nextV);
    const y0 = i * (segH + segGap);
    const y1 = y0 + segH;
    return { topHW, botHW, y0, y1 };
  });

  // For projection band, compute the band centered on the projection
  // segment using ±15% of projection value.
  const projIdx = stages.findIndex((s) => s.isProjection);
  const projGeom = geoms[projIdx];
  const projLo = enrollments * 0.85;
  const projHi = enrollments * 1.15;
  const bandLoHW = projGeom ? (funnelW / 2) * widthFrac(projLo) : 0;
  const bandHiHW = projGeom ? (funnelW / 2) * widthFrac(projHi) : 0;

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${totalW} ${totalH}`}
        className="w-full h-auto"
        role="img"
        aria-label="Engagement funnel"
      >
        {/* Funnel segments */}
        {stages.map((s, i) => {
          const g = geoms[i];
          const cx = funnelCx;
          // Trapezoid points: top-left, top-right, bottom-right, bottom-left
          const points = [
            [cx - g.topHW, g.y0],
            [cx + g.topHW, g.y0],
            [cx + g.botHW, g.y1],
            [cx - g.botHW, g.y1],
          ]
            .map((p) => p.join(','))
            .join(' ');
          return (
            <g key={s.label}>
              <polygon
                points={points}
                fill={s.color}
                fillOpacity={0.85}
                stroke={s.color}
                strokeWidth={0.5}
              />
              {/* Projection confidence band overlay */}
              {s.isProjection && projGeom && (
                <>
                  <line
                    x1={cx - bandLoHW}
                    x2={cx + bandLoHW}
                    y1={(projGeom.y0 + projGeom.y1) / 2}
                    y2={(projGeom.y0 + projGeom.y1) / 2}
                    stroke="#065f46"
                    strokeWidth={1}
                    strokeDasharray="2 2"
                  />
                  <line
                    x1={cx - bandHiHW}
                    x2={cx + bandHiHW}
                    y1={(projGeom.y0 + projGeom.y1) / 2 - 4}
                    y2={(projGeom.y0 + projGeom.y1) / 2 - 4}
                    stroke="#065f46"
                    strokeWidth={1}
                    strokeDasharray="2 2"
                  />
                </>
              )}
            </g>
          );
        })}

        {/* Connector lines (subtle separators between segments) */}
        {geoms.slice(0, -1).map((g, i) => (
          <line
            key={`sep-${i}`}
            x1={funnelCx - g.botHW}
            x2={funnelCx + g.botHW}
            y1={g.y1 + segGap / 2}
            y2={g.y1 + segGap / 2}
            stroke="#ffffff"
            strokeWidth={1.5}
          />
        ))}

        {/* Right-side labels */}
        {stages.map((s, i) => {
          const g = geoms[i];
          const yMid = (g.y0 + g.y1) / 2;
          const conv = s.prior != null && s.prior > 0 ? pctOf(s.value, s.prior) : null;
          return (
            <g key={`txt-${s.label}`}>
              <text
                x={funnelW + 16}
                y={yMid - 4}
                className="text-[12px] fill-gray-900 font-medium"
              >
                {s.label}
              </text>
              <text
                x={funnelW + 16}
                y={yMid + 11}
                className="text-[11px] fill-gray-700 tabular-nums"
              >
                {fmt(s.value)}
                {conv && (
                  <tspan className="fill-gray-500"> · {conv} of prior</tspan>
                )}
              </text>
            </g>
          );
        })}
      </svg>
      {enrollmentRatio != null && (
        <div className="mt-2 text-[10px] text-gray-500 leading-snug">
          Projection band (dashed) = replies × {enrollmentRatio.toFixed(2)} ± 15%
          confidence. Calibration source: federation-specific ratio. Funnel
          width is log-scaled so low-conversion segments remain visible.
        </div>
      )}
    </div>
  );
};

export default EngagementFunnelChart;
